// src/components/ReadjudicacionMasiva/Abogados/AsignacionPanel.js

import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Grid,
    Chip,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import AbogadoAutocomplete from './AbogadoAutocomplete';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3)
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    filters: {
        marginBottom: theme.spacing(3)
    },
    tableContainer: {
        maxHeight: 500,
        marginBottom: theme.spacing(2)
    },
    selectedChip: {
        marginLeft: theme.spacing(1)
    },
    actionsBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    abogadoSelector: {
        flexGrow: 1,
        maxWidth: 400
    },
    asignadoRow: {
        backgroundColor: '#e8f5e9 !important'
    },
    asignadoChip: {
        backgroundColor: '#4caf50',
        color: 'white',
        fontWeight: 600,
        fontSize: '0.75rem'
    },
    sinAsignarRow: {
        backgroundColor: '#fff3e0'
    },
    chipFiltro: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            opacity: 0.85,
            transform: 'scale(1.05)'
        }
    },
    chipActivo: {
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.5)'
    }
}));

const AsignacionPanel = ({
    registros,
    onAsignar,
    loading
}) => {
    const classes = useStyles();

    const [abogadoSeleccionado, setAbogadoSeleccionado] = useState(null);
    const [seleccionados, setSeleccionados] = useState([]);
    const [filtroAsignacion, setFiltroAsignacion] = useState(null); // null = todos, 'asignado', 'pendiente'
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });
    const showSnack = (message, severity = 'error') => setSnack({ open: true, message, severity });

    // Filtrar registros sin asignar (para el checkbox "seleccionar todos")
    const registrosSinAsignar = registros.filter(r => !r.AbogadoAsignado);
    const registrosConAsignado = registros.filter(r => r.AbogadoAsignado);

    // Registros visibles según filtro de chips
    const registrosMostrados = filtroAsignacion === 'asignado'
        ? registrosConAsignado
        : filtroAsignacion === 'pendiente'
            ? registrosSinAsignar
            : registros;

    const handleChipFiltro = (tipo) => {
        setFiltroAsignacion(prev => prev === tipo ? null : tipo);
        setSeleccionados([]);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Solo seleccionar los que NO tienen abogado asignado (de los visibles)
            const sinAsignarVisibles = registrosMostrados.filter(r => !r.AbogadoAsignado);
            setSeleccionados(sinAsignarVisibles.map(r => r.Id));
        } else {
            setSeleccionados([]);
        }
    };

    const handleSelectOne = (id) => {
        setSeleccionados(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleAsignar = () => {
        if (seleccionados.length === 0) {
            showSnack('Debe seleccionar al menos una causa', 'warning');
            return;
        }
        if (!abogadoSeleccionado) {
            showSnack('Debe seleccionar un abogado', 'warning');
            return;
        }

        onAsignar(abogadoSeleccionado.Id, seleccionados);
        // Limpiar seleccion despues de asignar
        setSeleccionados([]);
    };

    const isSelected = (id) => seleccionados.includes(id);
    const tieneAsignado = (registro) => registro.AbogadoAsignado && registro.AbogadoAsignado.trim() !== '';

    return (
        <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.title}>
                <PersonAddIcon color="primary" />
                Asignar Abogados
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
                Seleccione el abogado y marque las causas donde se debe asignar.
                Las filas en <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px' }}>verde</span> ya tienen abogado asignado.
            </Typography>

            {/* Resumen de asignaciones (clickeable como filtro) */}
            <Box mb={2} display="flex" style={{ gap: 8 }}>
                <Chip
                    label={`${registros.length} Total`}
                    size="small"
                    color={filtroAsignacion === null ? 'primary' : 'default'}
                    onClick={() => handleChipFiltro(null)}
                    className={`${classes.chipFiltro} ${filtroAsignacion === null ? classes.chipActivo : ''}`}
                />
                <Chip
                    label={`${registrosConAsignado.length} con abogado asignado`}
                    size="small"
                    style={{
                        backgroundColor: filtroAsignacion === 'asignado' ? '#2e7d32' : '#4caf50',
                        color: 'white'
                    }}
                    onClick={() => handleChipFiltro('asignado')}
                    className={`${classes.chipFiltro} ${filtroAsignacion === 'asignado' ? classes.chipActivo : ''}`}
                />
                <Chip
                    label={`${registrosSinAsignar.length} pendientes de asignar`}
                    size="small"
                    style={{
                        backgroundColor: filtroAsignacion === 'pendiente' ? '#e65100' : '#ff9800',
                        color: 'white'
                    }}
                    onClick={() => handleChipFiltro('pendiente')}
                    className={`${classes.chipFiltro} ${filtroAsignacion === 'pendiente' ? classes.chipActivo : ''}`}
                />
            </Box>

            {/* Selector de abogado */}
            <Box className={classes.filters}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <AbogadoAutocomplete
                            value={abogadoSeleccionado}
                            onChange={setAbogadoSeleccionado}
                            disabled={loading}
                            label="Abogado a Asignar"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Tabla */}
            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Tooltip title="Seleccionar todas las causas sin asignar (visibles)">
                                    <Checkbox
                                        indeterminate={seleccionados.length > 0 && seleccionados.length < registrosMostrados.filter(r => !r.AbogadoAsignado).length}
                                        checked={registrosMostrados.filter(r => !r.AbogadoAsignado).length > 0 && seleccionados.length === registrosMostrados.filter(r => !r.AbogadoAsignado).length}
                                        onChange={handleSelectAll}
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Fila</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Nombre</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Certificado</TableCell>
                            <TableCell style={{ fontWeight: 600, minWidth: 180 }}>Abogado Asignado</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Juzgado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {registrosMostrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No hay registros disponibles
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            registrosMostrados.map((registro, idx) => {
                                const asignado = tieneAsignado(registro);
                                return (
                                    <TableRow
                                        key={registro.Id}
                                        hover
                                        selected={isSelected(registro.Id)}
                                        onClick={() => handleSelectOne(registro.Id)}
                                        style={{ cursor: 'pointer' }}
                                        className={asignado ? classes.asignadoRow : ''}
                                        sx={{ backgroundColor: !asignado ? (idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent") : undefined }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={isSelected(registro.Id)} />
                                        </TableCell>
                                        <TableCell>{registro.Fila}</TableCell>
                                        <TableCell>{registro.Campo1}</TableCell>
                                        <TableCell>{registro.Campo3}</TableCell>
                                        <TableCell>
                                            {asignado ? (
                                                <Chip
                                                    icon={<CheckCircleIcon style={{ color: 'white' }} />}
                                                    label={registro.AbogadoAsignado}
                                                    size="small"
                                                    className={classes.asignadoChip}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Sin asignar
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap style={{ maxWidth: 150 }}>
                                                {registro.JuzgadoRadicacion || '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Barra de acciones */}
            <Box className={classes.actionsBar}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {seleccionados.length} causa(s) seleccionada(s)
                        {seleccionados.length > 0 && (
                            <Chip
                                label={`${seleccionados.length}`}
                                size="small"
                                color="primary"
                                className={classes.selectedChip}
                            />
                        )}
                    </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    {abogadoSeleccionado && (
                        <Chip
                            label={abogadoSeleccionado.NombreCompleto}
                            color="primary"
                            onDelete={() => setAbogadoSeleccionado(null)}
                        />
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                        onClick={handleAsignar}
                        disabled={loading || seleccionados.length === 0 || !abogadoSeleccionado}
                    >
                        Asignar
                    </Button>
                </Box>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(prev => ({...prev, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert elevation={6} variant="filled" severity={snack.severity} onClose={() => setSnack(prev => ({...prev, open: false}))}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default AsignacionPanel;
